/**
 * Join screen. Auto-fires `joinMatch` when we have a welcome and shows
 * a spinner otherwise. Server-side handling of "room gone" turns into
 * a matchPhase/error signal — for MVP we detect that indirectly via
 * an `error` message (the server closes on unknown matchId with
 * `error:{ code: "notInMatch" }` per WP5 spec). We surface a friendly
 * "match unavailable" if we never get a `matchJoined` ack (#742) within a
 * short grace window.
 */

import { useEffect, useRef, useState, type ReactElement } from "react";
import { toMatchId, type AbilityId, type MatchId } from "@pit/protocol";
import { useWsClient } from "../net/WsClientProvider.js";
import { useStore } from "../store/context.js";
import { navigate } from "../router/hashRoute.js";
import { ChampionPicker } from "../champions/ChampionPicker.js";
import { DEFAULT_CHAMPION_ID } from "../champions/catalog.js";
import { LoadoutPicker, isValidLoadoutSize } from "../champions/LoadoutPicker.js";
import { defaultLoadout } from "../champions/loadout.js";

/** Grace period before we conclude the room doesn't exist. */
export const JOIN_GRACE_MS = 5_000;

export function JoinScreen({ matchId }: { readonly matchId: string }): ReactElement {
  const client = useWsClient();
  const { state, dispatch } = useStore();
  const attempted = useRef(false);
  const [timedOut, setTimedOut] = useState(false);
  const [championId, setChampionId] = useState(DEFAULT_CHAMPION_ID);
  // #323: see HomeScreen — same default-then-adjust loadout flow.
  const [loadout, setLoadout] = useState<readonly AbilityId[]>(() =>
    defaultLoadout(DEFAULT_CHAMPION_ID),
  );
  const loadoutValid = isValidLoadoutSize(championId, loadout);
  // The join is now explicit: the player picks a champion and clicks Join.
  // We only fire `joinMatch` once `confirmed` flips true, so the champion
  // choice is always sent (unlike the old auto-join-on-welcome flow).
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (!confirmed) return;
    if (state.connection.kind !== "welcomed") return;
    if (attempted.current) return;
    attempted.current = true;
    let id: MatchId;
    try {
      id = toMatchId(matchId);
    } catch {
      return;
    }
    client.joinMatch(id, championId, [...loadout]);
    dispatch({ type: "match.champion", championId, loadout });
    // #742: the server now sends an explicit `matchJoined` ack (matchId +
    // the real, join-order-assigned seat) — `WsClientProvider`'s message
    // handler dispatches `match.joined` from it directly. This screen no
    // longer needs to optimistically guess `state.matchId`/`ownSeat`
    // itself; the old `ownSeat: 1` guess ("seat 1 is the joiner
    // convention") was silently wrong for a 3v3 practice joiner landing in
    // any of seats 1-5. If the server rejects the join it sends
    // `error:badMessage` + close, and the grace-window fallback below
    // still catches missing-room cases (now actually exercised — see the
    // timer below).
    const timer = window.setTimeout(() => {
      // If matchId is still null after grace, treat as failed join.
      // (Server will otherwise have sent matchPhase / snapshot by now.)
      if (state.matchId === null) setTimedOut(true);
    }, JOIN_GRACE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [
    confirmed,
    championId,
    loadout,
    client,
    dispatch,
    matchId,
    state.connection.kind,
    state.matchId,
  ]);

  // Once matchId is populated, hop to the Match screen.
  useEffect(() => {
    if (state.matchId !== null && state.connection.kind === "welcomed") {
      navigate({ kind: "match" });
    }
  }, [state.matchId, state.connection.kind]);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        flexDirection: "column",
        gap: 16,
      }}
    >
      {timedOut ? (
        <>
          <h2 style={{ margin: 0 }}>Match unavailable</h2>
          <p style={{ opacity: 0.75 }}>
            The room <code>{matchId}</code> could not be found.
          </p>
          <button
            type="button"
            onClick={() => {
              dispatch({ type: "match.leave" });
              navigate({ kind: "home" });
            }}
            style={{ padding: "8px 16px" }}
          >
            Back to home
          </button>
        </>
      ) : confirmed ? (
        <>
          <h2 style={{ margin: 0 }}>Joining {matchId}…</h2>
          <div style={{ opacity: 0.6 }}>
            {state.connection.kind === "welcomed" ? "waiting for server" : "connecting…"}
          </div>
        </>
      ) : (
        <>
          <h2 style={{ margin: 0 }}>Join {matchId}</h2>
          <ChampionPicker
            value={championId}
            onChange={(id) => {
              setChampionId(id);
              setLoadout(defaultLoadout(id));
            }}
            disabled={state.connection.kind !== "welcomed"}
          />
          <LoadoutPicker
            championId={championId}
            value={loadout}
            onChange={setLoadout}
            disabled={state.connection.kind !== "welcomed"}
          />
          <button
            type="button"
            data-testid="join-confirm"
            disabled={state.connection.kind !== "welcomed" || !loadoutValid}
            onClick={() => {
              setConfirmed(true);
            }}
            style={{
              padding: "10px 24px",
              fontSize: 15,
              background: state.connection.kind === "welcomed" ? "#3aa675" : "#333",
              color: "#fff",
              border: "none",
              borderRadius: 6,
            }}
          >
            Join match
          </button>
          {state.connection.kind !== "welcomed" ? (
            <div style={{ opacity: 0.6 }}>connecting…</div>
          ) : null}
        </>
      )}
    </div>
  );
}
